import React, { useEffect } from 'react'
import {
  withRouter,
  RouteComponentProps,
  useRouteMatch,
} from 'react-router-dom'
import queryString from 'query-string'
import {
  useAnalytics,
  EmptyHeader,
  routes,
  QueryParams,
  useSignOut,
  useGetWorkspaces,
  LoadingPage,
  GenericErrorView,
  useUserSignedIn,
} from '@sketch/modules-common'
import { useToast } from '@sketch/toasts'

import {
  GetVerifiedUserDocument,
  useEmailVerifyMutation,
} from '@sketch/gql-types'
import { getItem, removeItem } from '@sketch/utils/src/sessionStorage'
import { sessionStorageKeys } from '@sketch/constants'

interface EmailVerifyViewProps extends RouteComponentProps {}

type EmailVerifyQueryParams = QueryParams<'ACCEPT'> | QueryParams<'CONFIRM'>

/**
 * This component is used to validate a user email.
 * Example URL: https://staging.sketch.cloud/accept?token=e2f0f334-9d86-43af-912a-2b7d178b142b
 * It's used in the "Verify your Sketch Cloud account" email
 * and redirects the user to it's home-view if everything is alright.
 *
 * It also works for the `confirm` query string, which is used to confirm an
 * email that has changed.
 * Example URL: https://staging.sketch.cloud/confirm?token=e2f0f334-9d86-43af-912a-2b7d178b142b
 */
const EmailVerifyView = (props: EmailVerifyViewProps) => {
  const isSignedIn = useUserSignedIn()

  const { token } = queryString.parse(
    props.location.search
  ) as EmailVerifyQueryParams

  const isConfirmEmailChangeRoute = useRouteMatch({
    path: routes.CONFIRM.template(),
    exact: true,
  })

  const { showToast } = useToast()
  const { trackEvent } = useAnalytics()
  const { data, error, loading } = useGetWorkspaces({
    skip: !isSignedIn,
  })

  const workspace = data?.me.workspaces.find(w => w.identifier)

  const signOut = useSignOut({
    reason: 'Confirming email',
  })

  const refetchVerifiedUser = !isConfirmEmailChangeRoute && isSignedIn

  const [verifyToken] = useEmailVerifyMutation({
    redirectErrors: false,
    onError: error => {
      showToast(error.message, 'negative')
    },
    ...(refetchVerifiedUser && {
      refetchQueries: [{ query: GetVerifiedUserDocument }],
      awaitRefetchQueries: true,
    }),
    onCompleted: data => {
      const { email, successful, errors } = data?.emailVerify || {}

      if (successful) {
        if (isConfirmEmailChangeRoute) {
          showToast(
            `${
              email ?? 'Your email'
            } successfully verified. Please sign in again.`,
            'positive'
          )

          signOut()
          return
        } else {
          trackEvent('ONBOARDING - email verified')

          showToast(
            <>
              All set! We’ve verified <strong>{email}</strong>
            </>,
            'positive'
          )
        }
      } else if (errors && errors.length > 0) {
        const apiErrors = errors.map(error => error?.message).join('; ')

        showToast(apiErrors, 'negative')

        props.history.replace(routes.CHECK_YOUR_INBOX.create({}))

        return
      } else {
        showToast('Unable to verify your email', 'negative')
      }

      const isPricingSource = !!getItem(sessionStorageKeys.source)

      if (isPricingSource && workspace) {
        removeItem(sessionStorageKeys.source)

        props.history.replace(
          routes.WORKSPACE_SUBSCRIBE.create({
            workspaceId: workspace.identifier,
          })
        )

        return
      }

      props.history.replace(routes.CHOOSE_YOUR_PATH.create({}))
    },
  })

  useEffect(() => {
    if (token) {
      verifyToken({
        variables: { token },
      })
    }
  }, [token, verifyToken])

  if (loading) {
    return <LoadingPage hideFooter />
  }

  if (error) {
    return <GenericErrorView />
  }

  return <EmptyHeader />
}

export default withRouter(EmailVerifyView)
